import {
    AddCircleOutline,
    AdminPanelSettings,
    Block,
    CheckCircle,
    ExpandLess,
    ExpandMore,
    Person,
    RemoveCircleOutline,
    Search,
    Shield,
    Storage,
} from '@mui/icons-material';
import {
    Box,
    Card,
    CardContent,
    Chip,
    Collapse,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Button,
    IconButton,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AdminUserView } from '@/domain/entities/user';
import { adminApi, ApiError } from '@/infrastructure/services/api-client';
import { useNotificationStore } from '@/stores/use-notification-store';
import { useThemeStore } from '@/stores/use-theme-store';

function extractDomain(email: string): string {
    return email.split('@')[1]?.toLowerCase() ?? '';
}

const PLAN_IDS = ['mcp-abap', 'mcp-btp'] as const;

function planLabel(planId: string): string {
    return planId.replace('mcp-', '').toUpperCase();
}

type ConfirmAction = {
    type: 'role' | 'status' | 'grant-plan' | 'revoke-plan';
    user: AdminUserView;
    planId?: string;
};

export function AdminUsersPage() {
    const { t } = useTranslation();
    const { addNotification } = useNotificationStore();
    const { mode } = useThemeStore();

    const [users, setUsers] = useState<AdminUserView[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
    const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [allowedDomains, setAllowedDomains] = useState<Set<string>>(new Set());

    const fetchData = useCallback(async () => {
        try {
            const [usersResult, domainsResult] = await Promise.all([
                adminApi.listUsers(),
                adminApi.listDomains(),
            ]);
            setUsers(usersResult.users);
            setAllowedDomains(new Set(domainsResult.domains.map((d) => d.domain.toLowerCase())));
        } catch (error) {
            if (error instanceof ApiError) {
                addNotification(error.message, 'error');
            }
        } finally {
            setLoading(false);
        }
    }, [addNotification]);

    useEffect(() => {
        void fetchData();
    }, [fetchData]);

    const filteredUsers = useMemo(() => {
        if (!search.trim()) return users;
        const q = search.toLowerCase();
        return users.filter(
            (u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q),
        );
    }, [users, search]);

    const handleToggleRole = async (user: AdminUserView) => {
        setActionLoading(true);
        try {
            const result = await adminApi.toggleRole(user.id);
            setUsers((prev) =>
                prev.map((u) => (u.id === result.id ? { ...u, role: result.role } : u)),
            );
            addNotification(t('admin.users.roleUpdated'), 'success');
        } catch (error) {
            if (error instanceof ApiError) {
                addNotification(error.message, 'error');
            }
        } finally {
            setActionLoading(false);
            setConfirmAction(null);
        }
    };

    const handleToggleStatus = async (user: AdminUserView) => {
        setActionLoading(true);
        try {
            const result = await adminApi.toggleStatus(user.id);
            setUsers((prev) =>
                prev.map((u) => (u.id === result.id ? { ...u, status: result.status } : u)),
            );
            addNotification(t('admin.users.statusUpdated'), 'success');
        } catch (error) {
            if (error instanceof ApiError) {
                addNotification(error.message, 'error');
            }
        } finally {
            setActionLoading(false);
            setConfirmAction(null);
        }
    };

    const handleGrantPlan = async (user: AdminUserView, planId: string) => {
        setActionLoading(true);
        try {
            await adminApi.grantPlan(user.id, planId);
            addNotification(t('admin.users.planGranted'), 'success');
            await fetchData();
        } catch (error) {
            if (error instanceof ApiError) {
                addNotification(error.message, 'error');
            }
        } finally {
            setActionLoading(false);
            setConfirmAction(null);
        }
    };

    const handleRevokePlan = async (user: AdminUserView, planId: string) => {
        setActionLoading(true);
        try {
            await adminApi.revokePlan(user.id, planId);
            addNotification(t('admin.users.planRevoked'), 'success');
            await fetchData();
        } catch (error) {
            if (error instanceof ApiError) {
                addNotification(error.message, 'error');
            }
        } finally {
            setActionLoading(false);
            setConfirmAction(null);
        }
    };

    const handleConfirm = async () => {
        if (!confirmAction) return;
        if (confirmAction.type === 'role') {
            await handleToggleRole(confirmAction.user);
        } else if (confirmAction.type === 'status') {
            await handleToggleStatus(confirmAction.user);
        } else if (confirmAction.type === 'grant-plan' && confirmAction.planId) {
            await handleGrantPlan(confirmAction.user, confirmAction.planId);
        } else if (confirmAction.type === 'revoke-plan' && confirmAction.planId) {
            await handleRevokePlan(confirmAction.user, confirmAction.planId);
        }
    };

    const getConfirmMessage = (): string => {
        if (!confirmAction) return '';
        if (confirmAction.type === 'role') {
            return confirmAction.user.role === 'admin'
                ? t('admin.users.confirmDemote')
                : t('admin.users.confirmPromote');
        }
        if (confirmAction.type === 'grant-plan' && confirmAction.planId) {
            return t('admin.users.confirmGrantPlan', {
                plan: planLabel(confirmAction.planId),
                user: confirmAction.user.name,
            });
        }
        if (confirmAction.type === 'revoke-plan' && confirmAction.planId) {
            return t('admin.users.confirmRevokePlan', {
                plan: planLabel(confirmAction.planId),
                user: confirmAction.user.name,
            });
        }
        return confirmAction.user.status === 'active'
            ? t('admin.users.confirmDisable')
            : t('admin.users.confirmEnable');
    };

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    };

    const getActivePlans = (user: AdminUserView): string => {
        const active = user.subscriptions.filter((s) => s.status === 'active');
        if (active.length === 0) return '—';
        return active.map((s) => s.planId.replace('mcp-', '').toUpperCase()).join(', ');
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {t('admin.users.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {t('admin.users.subtitle')}
                </Typography>
            </Box>

            <TextField
                fullWidth
                size="small"
                placeholder={t('admin.users.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ mb: 3 }}
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search color="action" />
                            </InputAdornment>
                        ),
                    },
                }}
            />

            <Card
                sx={{
                    border: '1px solid',
                    borderColor: mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                }}
            >
                <CardContent sx={{ p: 0 }}>
                    {loading ? (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <Typography color="text.secondary">{t('common.loading')}</Typography>
                        </Box>
                    ) : filteredUsers.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <Typography color="text.secondary">{t('common.noData')}</Typography>
                        </Box>
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell width={40} />
                                        <TableCell>{t('admin.users.name')}</TableCell>
                                        <TableCell>{t('admin.users.email')}</TableCell>
                                        <TableCell>{t('admin.users.domain')}</TableCell>
                                        <TableCell>{t('admin.users.role')}</TableCell>
                                        <TableCell>{t('admin.users.status')}</TableCell>
                                        <TableCell>{t('admin.users.plans')}</TableCell>
                                        <TableCell>{t('admin.users.apiKeys')}</TableCell>
                                        <TableCell>{t('admin.users.createdAt')}</TableCell>
                                        <TableCell align="right">{t('admin.users.actions')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredUsers.map((user) => (
                                        <UserRow
                                            key={user.id}
                                            user={user}
                                            expanded={expandedUserId === user.id}
                                            onToggleExpand={() =>
                                                setExpandedUserId(expandedUserId === user.id ? null : user.id)
                                            }
                                            onToggleRole={() => setConfirmAction({ type: 'role', user })}
                                            onToggleStatus={() => setConfirmAction({ type: 'status', user })}
                                            onGrantPlan={(planId) => setConfirmAction({ type: 'grant-plan', user, planId })}
                                            onRevokePlan={(planId) => setConfirmAction({ type: 'revoke-plan', user, planId })}
                                            formatDate={formatDate}
                                            getActivePlans={getActivePlans}
                                            allowedDomains={allowedDomains}
                                            t={t}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>

            <Dialog
                open={!!confirmAction}
                onClose={() => !actionLoading && setConfirmAction(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 600 }}>{t('common.confirm')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{getConfirmMessage()}</DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setConfirmAction(null)} disabled={actionLoading}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        color={
                            (confirmAction?.type === 'status' && confirmAction.user.status === 'active') ||
                            confirmAction?.type === 'revoke-plan'
                                ? 'error'
                                : 'primary'
                        }
                        onClick={handleConfirm}
                        disabled={actionLoading}
                    >
                        {actionLoading ? t('common.loading') : t('common.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

interface UserRowProps {
    user: AdminUserView;
    expanded: boolean;
    onToggleExpand: () => void;
    onToggleRole: () => void;
    onToggleStatus: () => void;
    onGrantPlan: (planId: string) => void;
    onRevokePlan: (planId: string) => void;
    formatDate: (date: string) => string;
    getActivePlans: (user: AdminUserView) => string;
    allowedDomains: Set<string>;
    t: (key: string, params?: Record<string, string>) => string;
}

function UserRow({
    user,
    expanded,
    onToggleExpand,
    onToggleRole,
    onToggleStatus,
    onGrantPlan,
    onRevokePlan,
    formatDate,
    getActivePlans,
    allowedDomains,
    t,
}: UserRowProps) {
    const userDomain = extractDomain(user.email);
    const isWhitelisted = allowedDomains.has(userDomain);

    return (
        <>
            <TableRow sx={{ '& > *': { borderBottom: expanded ? 'unset' : undefined } }}>
                <TableCell>
                    <IconButton size="small" onClick={onToggleExpand}>
                        {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    </IconButton>
                </TableCell>
                <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {user.name}
                    </Typography>
                </TableCell>
                <TableCell>
                    <Typography variant="body2" color="text.secondary">
                        {user.email}
                    </Typography>
                </TableCell>
                <TableCell>
                    <Chip
                        label={isWhitelisted ? t('admin.users.whitelisted') : t('admin.users.waitlisted')}
                        size="small"
                        color={isWhitelisted ? 'success' : 'default'}
                        variant="outlined"
                    />
                </TableCell>
                <TableCell>
                    <Chip
                        icon={user.role === 'admin' ? <Shield fontSize="small" /> : <Person fontSize="small" />}
                        label={user.role === 'admin' ? t('admin.users.roleAdmin') : t('admin.users.roleUser')}
                        size="small"
                        color={user.role === 'admin' ? 'warning' : 'default'}
                        variant="outlined"
                    />
                </TableCell>
                <TableCell>
                    <Chip
                        label={user.status === 'active' ? t('admin.users.statusActive') : t('admin.users.statusDisabled')}
                        size="small"
                        color={user.status === 'active' ? 'success' : 'error'}
                        variant="outlined"
                    />
                </TableCell>
                <TableCell>
                    <Typography variant="body2">{getActivePlans(user)}</Typography>
                </TableCell>
                <TableCell>
                    <Typography variant="body2">{user.apiKeysCount}</Typography>
                </TableCell>
                <TableCell>
                    <Typography variant="body2" color="text.secondary">
                        {formatDate(user.createdAt)}
                    </Typography>
                </TableCell>
                <TableCell align="right">
                    {PLAN_IDS.map((pid) => {
                        const activeSub = user.subscriptions.find(
                            (s) => s.planId === pid && s.status === 'active',
                        );
                        if (activeSub?.adminGranted) {
                            return (
                                <Tooltip key={pid} title={`${t('admin.users.revokePlan')} ${planLabel(pid)}`}>
                                    <IconButton size="small" onClick={() => onRevokePlan(pid)} color="error">
                                        <RemoveCircleOutline fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            );
                        }
                        if (!activeSub) {
                            return (
                                <Tooltip key={pid} title={`${t('admin.users.grantPlan')} ${planLabel(pid)}`}>
                                    <IconButton size="small" onClick={() => onGrantPlan(pid)} color="success">
                                        <AddCircleOutline fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            );
                        }
                        return null;
                    })}
                    <Tooltip
                        title={
                            user.role === 'admin'
                                ? t('admin.users.demoteUser')
                                : t('admin.users.promoteAdmin')
                        }
                    >
                        <IconButton size="small" onClick={onToggleRole} color="warning">
                            <AdminPanelSettings fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip
                        title={
                            user.status === 'active'
                                ? t('admin.users.disableAccount')
                                : t('admin.users.enableAccount')
                        }
                    >
                        <IconButton
                            size="small"
                            onClick={onToggleStatus}
                            color={user.status === 'active' ? 'error' : 'success'}
                        >
                            {user.status === 'active' ? (
                                <Block fontSize="small" />
                            ) : (
                                <CheckCircle fontSize="small" />
                            )}
                        </IconButton>
                    </Tooltip>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell sx={{ py: 0 }} colSpan={10}>
                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, px: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                <Storage fontSize="small" color="action" />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    {t('admin.users.sapEnvironments')}
                                </Typography>
                            </Box>
                            {user.sapEnvironments.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ pl: 3.5 }}>
                                    {t('admin.users.noSapEnvironments')}
                                </Typography>
                            ) : (
                                <Table size="small" sx={{ ml: 3.5 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t('admin.users.identifier')}</TableCell>
                                            <TableCell>{t('admin.users.description')}</TableCell>
                                            <TableCell>{t('admin.users.host')}</TableCell>
                                            <TableCell>{t('admin.users.client')}</TableCell>
                                            <TableCell>{t('admin.users.username')}</TableCell>
                                            <TableCell>{t('admin.users.language')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {user.sapEnvironments.map((env) => (
                                            <TableRow key={env.id}>
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontFamily: '"JetBrains Mono", monospace',
                                                            fontSize: '0.75rem',
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        {env.identifier}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">{env.description}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontFamily: '"JetBrains Mono", monospace',
                                                            fontSize: '0.75rem',
                                                        }}
                                                    >
                                                        {env.host}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">{env.client}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">{env.username}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">{env.language}</Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

export default AdminUsersPage;
